import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Ticket } from "lucide-react";
import { CouponCard } from "./CouponCard";
import type { DiscountResponse } from "../core/types/control";

interface CouponCarouselProps {
	coupons: DiscountResponse[];
}

export const CouponCarousel = ({ coupons }: CouponCarouselProps) => {
	const carouselRef = useRef<HTMLDivElement | null>(null);

	const [isAtStart, setIsAtStart] = useState(true);
	const [isAtEnd, setIsAtEnd] = useState(false);

	// Verificamos posición
	const checkScrollPosition = () => {
		if (!carouselRef.current) return;
		const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
		setIsAtStart(scrollLeft <= 5);
		setIsAtEnd(Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 5);
	};

	useEffect(() => {
		const carousel = carouselRef.current;
		if (carousel) {
			carousel.addEventListener("scroll", checkScrollPosition);
			window.addEventListener("resize", checkScrollPosition);
			setTimeout(checkScrollPosition, 100);
		}
		return () => {
			carousel?.removeEventListener("scroll", checkScrollPosition);
			window.removeEventListener("resize", checkScrollPosition);
		};
	}, [coupons]);

	// Función de scroll dinámica
	const scrollNext = () => {
		if (!carouselRef.current) return;
		if (isAtEnd) {
			carouselRef.current.scrollTo({ left: 0, behavior: "smooth" });
			return;
		}
		const cardWidth = carouselRef.current.clientWidth / 4; // Ajustado a 4 para un scroll más acorde a tamaños pequeños
		carouselRef.current.scrollBy({ left: cardWidth, behavior: "smooth" });
	};

	const scrollPrev = () => {
		if (!carouselRef.current) return;
		if (isAtStart) return;
		const cardWidth = carouselRef.current.clientWidth / 4; // Ajustado a 4
		carouselRef.current.scrollBy({ left: -cardWidth, behavior: "smooth" });
	};

	const hasCoupons = coupons && coupons.length > 0;

	return (
		// Reducimos py-12 a py-6
		<div className='bg-white py-5 sm:py-6 relative'>
			{/* Reducimos mb-6 a mb-4 y px-8 a px-6 */}
			<div className='px-4 sm:px-8 mb-3 sm:mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3'>
				<div>
					<h2 className='text-lg sm:text-xl font-bold text-gray-800'>
						{" "}
						{/* text-2xl a text-xl */}
						Cupones Disponibles
					</h2>
				</div>

				{hasCoupons && (
					<div className='flex gap-2 w-full sm:w-auto justify-start sm:justify-end'>
						{/* Reducimos el padding y tamaño de icono de los botones */}
						<button
							onClick={scrollPrev}
							disabled={isAtStart}
							className={`p-1.5 rounded-full border transition-all ${
								isAtStart
									? "bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed"
									: "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 shadow-sm cursor-pointer"
							}`}
						>
							<ChevronLeft size={20} />
						</button>

						<button
							onClick={scrollNext}
							className='p-1.5 rounded-full bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm transition-all active:scale-95 cursor-pointer'
						>
							<ChevronRight size={20} />
						</button>
					</div>
				)}
			</div>

			<div className='relative w-full px-4'>
				{" "}
				{/* px-6 a px-4 */}
				{!hasCoupons ? (
					// Reducimos el padding del empty state
					<div className='flex flex-col items-center justify-center py-6 px-4 bg-gray-50/50 rounded-2xl border border-gray-100 mx-2'>
						<div className='bg-gray-100 p-3 rounded-full mb-2 text-gray-400'>
							<Ticket size={24} /> {/* Icono más pequeño */}
						</div>
						<h3 className='text-gray-700 text-sm font-medium mb-1'>
							{" "}
							{/* text-sm en lugar de normal */}
							No hay cupones disponibles
						</h3>
						<p className='text-gray-500 text-xs text-center max-w-sm'>
							{" "}
							{/* text-xs en lugar de text-sm */}
							Actualmente no tienes ningún cupón de descuento asignado.
						</p>
					</div>
				) : (
					<div
						ref={carouselRef}
						className='flex overflow-x-auto scroll-smooth no-scrollbar snap-x snap-mandatory'
						style={{ scrollbarWidth: "none" }}
					>
						{coupons.map((coupon) => {
							const statusLabel = !coupon.is_active
								? "Inactivo"
								: coupon.remaining_uses !== null &&
									  coupon.remaining_uses <= 5
									? "Agotándose"
									: "Activo";

							const formattedDate = coupon.expiration_date
								? new Date(coupon.expiration_date).toLocaleDateString(
										"es-ES",
									)
								: undefined;

							const labelType =
								coupon.type === "discount" ? "Descuento" : coupon.type;

							return (
								<div
									key={coupon.id}
									// Hacemos que en desktop (lg) quepan más tarjetas (20% en lugar de 25%) y reducimos el padding
									className='min-w-full sm:min-w-[80%] md:min-w-[50%] lg:min-w-[25%] p-2 snap-start flex justify-center'
								>
									<div className='w-full'>
										<CouponCard
											code={coupon.code_discount}
											discount={coupon.weight_discount}
											type={labelType}
											validUntil={formattedDate}
											status={statusLabel}
										/>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};
